/**
 * Polygon union utility using clipper-lib (already in node_modules via leaflet-freedraw).
 * Falls back to convex hull if clipper-lib fails.
 *
 * Input:  array of polygons, each polygon = array of {lat, lng} objects
 * Output: single polygon as array of {lat, lng} objects, or null on failure
 */
import ClipperLib from 'clipper-lib';

// Clipper uses integer coordinates — we scale lat/lng by this factor
const SCALE = 1e7;

/**
 * Union multiple polygons into one.
 * @param {Array<Array<{lat: number, lng: number}>>} polygons - array of polygons
 * @returns {Array<{lat: number, lng: number}>|null} merged polygon paths, or null on failure
 */
export function unionPolygons(polygons) {
  if (!polygons || polygons.length === 0) return null;
  if (polygons.length === 1) return [...polygons[0]];

  try {
    return clipperUnion(polygons);
  } catch (err) {
    console.error('Clipper union error, falling back to convex hull:', err);
    return convexHullFallback(polygons);
  }
}

// ===== CLIPPER-LIB UNION =====

function toClipperPath(points) {
  return points.map(p => ({
    X: Math.round(p.lng * SCALE),
    Y: Math.round(p.lat * SCALE),
  }));
}

function fromClipperPath(path) {
  return path.map(p => ({
    lat: p.Y / SCALE,
    lng: p.X / SCALE,
  }));
}

function clipperUnion(polygons) {
  const clipper = new ClipperLib.Clipper();

  // Add first polygon as subject
  const subj = toClipperPath(polygons[0]);
  clipper.AddPath(subj, ClipperLib.PolyType.ptSubject, true);

  // Add remaining polygons as clips
  for (let i = 1; i < polygons.length; i++) {
    const clip = toClipperPath(polygons[i]);
    clipper.AddPath(clip, ClipperLib.PolyType.ptClip, true);
  }

  const solution = new ClipperLib.Paths();
  const success = clipper.Execute(
    ClipperLib.ClipType.ctUnion,
    solution,
    ClipperLib.PolyFillType.pftNonZero,
    ClipperLib.PolyFillType.pftNonZero
  );

  if (!success || solution.length === 0) {
    console.warn('Clipper union returned no result, falling back to convex hull');
    return convexHullFallback(polygons);
  }

  // Find the largest polygon in the solution (by area)
  let largest = null;
  let largestArea = 0;
  for (const path of solution) {
    const area = Math.abs(ClipperLib.Clipper.Area(path));
    if (area > largestArea) {
      largestArea = area;
      largest = path;
    }
  }

  return largest ? fromClipperPath(largest) : null;
}

// ===== CONVEX HULL FALLBACK =====
// If clipper-lib fails, merge all vertices and compute convex hull.
// This loses concavities but produces a valid merged polygon.

function convexHullFallback(polygons) {
  const allPoints = polygons.flat().map(p => ({ lat: p.lat, lng: p.lng }));
  if (allPoints.length < 3) return null;
  return convexHull(allPoints);
}

function cross(O, A, B) {
  return (A.lng - O.lng) * (B.lat - O.lat) - (A.lat - O.lat) * (B.lng - O.lng);
}

function convexHull(points) {
  const pts = points.slice().sort((a, b) => a.lng === b.lng ? a.lat - b.lat : a.lng - b.lng);
  if (pts.length <= 1) return pts;

  // Build lower hull
  const lower = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }

  // Build upper hull
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }

  // Remove last point of each half because it's repeated
  lower.pop();
  upper.pop();

  return lower.concat(upper);
}
